import { useState } from 'react';
import type { Workspace } from '@cm/protocol';
import { WORKSPACE_COLORS } from '@cm/protocol';
import { api } from '../api';
import { useStore } from '../store';

interface Props {
  /** Ausente = criando um projeto novo. */
  workspace?: Workspace;
  onClose: () => void;
}

export function WorkspaceDialog({ workspace, onClose }: Props) {
  const pushToast = useStore((s) => s.pushToast);
  const refreshWorkspaces = useStore((s) => s.refreshWorkspaces);
  const setActive = useStore((s) => s.setActiveWorkspace);
  const sessions = useStore((s) => s.sessions);

  const [name, setName] = useState(workspace?.name ?? '');
  const [cwd, setCwd] = useState(workspace?.cwd ?? '');
  const [color, setColor] = useState(workspace?.color ?? WORKSPACE_COLORS[0]);
  const [notes, setNotes] = useState(workspace?.notes ?? '');
  const [busy, setBusy] = useState(false);

  // Sugere o nome a partir da última parte do caminho, que quase sempre é o
  // nome do projeto — poupa digitação sem impedir a edição.
  function onCwdChange(value: string) {
    setCwd(value);
    const leaf = value.replace(/\/+$/, '').split('/').filter(Boolean).pop();
    if (leaf && (!name || name === suggestFrom(cwd))) setName(leaf);
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const body = { name: name.trim(), cwd: cwd.trim(), color, notes: notes.trim() || null };
      if (workspace) {
        await api.updateWorkspace(workspace.id, body);
        pushToast({ kind: 'success', text: 'projeto atualizado' });
      } else {
        const { workspace: created } = await api.createWorkspace({ ...body, archived: false });
        setActive(created.id);
        pushToast({ kind: 'success', text: `projeto "${created.name}" criado` });
      }
      await refreshWorkspaces();
      onClose();
    } catch (err) {
      pushToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!workspace) return;
    const live = sessions.filter(
      (s) => s.workspaceId === workspace.id && s.status === 'running',
    ).length;
    const extra = live > 0 ? `\n\nAtenção: há ${live} sessão(ões) rodando neste projeto.` : '';
    if (!window.confirm(`Excluir o projeto "${workspace.name}"?${extra}`)) return;

    setBusy(true);
    try {
      await api.deleteWorkspace(workspace.id);
      await refreshWorkspaces();
      pushToast({ kind: 'info', text: 'projeto excluído' });
      onClose();
    } catch (err) {
      pushToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <form className="modal" onMouseDown={(e) => e.stopPropagation()} onSubmit={submit}>
        <h2>{workspace ? 'Editar projeto' : 'Novo projeto'}</h2>

        <div className="field">
          <label htmlFor="ws-cwd">Pasta do projeto</label>
          <input
            id="ws-cwd"
            value={cwd}
            onChange={(e) => onCwdChange(e.target.value)}
            placeholder="/var/www/html/meu-projeto"
            autoFocus={!workspace}
          />
          <p className="card-desc" style={{ marginTop: 6, marginBottom: 0 }}>
            Todos os agentes deste projeto abrem nesta pasta.
          </p>
        </div>

        <div className="field">
          <label htmlFor="ws-name">Nome</label>
          <input id="ws-name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <div className="field">
          <label>Cor da aba</label>
          <div className="color-grid">
            {WORKSPACE_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={`color-swatch${c === color ? ' selected' : ''}`}
                style={{ background: c }}
                onClick={() => setColor(c)}
                title={c}
              />
            ))}
          </div>
        </div>

        <div className="field">
          <label htmlFor="ws-notes">Anotações (opcional)</label>
          <textarea
            id="ws-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Contexto do projeto, convenções, o que está em andamento…"
            style={{ minHeight: 70 }}
          />
        </div>

        <div className="row" style={{ marginTop: 4 }}>
          {workspace && (
            <button type="button" className="ghost danger" onClick={() => void remove()} disabled={busy}>
              excluir
            </button>
          )}
          <span className="grow" />
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button className="primary" type="submit" disabled={busy || !name.trim() || !cwd.trim()}>
            {busy ? 'salvando…' : workspace ? 'Salvar' : 'Criar projeto'}
          </button>
        </div>
      </form>
    </div>
  );
}

function suggestFrom(path: string): string {
  return path.replace(/\/+$/, '').split('/').filter(Boolean).pop() ?? '';
}
